import { useCallback } from 'react'

import { useWorkflows, useWorkflowSchema } from 'data/hooks/workflows/workflows'
import { WorkflowSchemaActionStateItemAvailability } from 'data/hooks/workflowTypes'
import { convertOutputSchemaToOutputs } from 'features/workflows/OutputSchema/utils'

export function useInvokeFlowActionFinalSchema() {
    const { data: workflows } = useWorkflows()
    const { data: schema } = useWorkflowSchema()

    return useCallback(
        (config: WorkflowActionConfig): WorkflowSchemaNodeType | undefined => {
            const type = schema?.nodes.find(
                (t) => t.id === config.action_type
            ) as WorkflowSchemaActionType

            if (!type || !config.settings?.flow_id) {
                return undefined
            }

            const selectedFlow = workflows?.find((w) => w._sid === config.settings?.flow_id)

            if (!selectedFlow || selectedFlow.trigger?.trigger_type !== 'invoked') {
                return undefined
            }

            const triggerSchema = selectedFlow.trigger?.settings?.trigger_schema as
                | WorkflowOutputSchema
                | undefined
            const returnSchema = selectedFlow.trigger?.settings?.return_schema as
                | WorkflowOutputSchema
                | undefined

            // Convert trigger_schema to inputs
            let dynamicInputs: (WorkflowSchemaNodeInput | WorkflowSchemaNodeInputGroup)[] = []
            if (triggerSchema) {
                dynamicInputs = convertOutputSchemaToInputs(triggerSchema)
            }

            // Convert return_schema to state outputs
            let flow_result:
                | WorkflowSchemaActionStateItem
                | WorkflowSchemaStateItemGroup
                | undefined = undefined
            if (!returnSchema) {
                flow_result = {
                    id: 'result',
                    name: 'Flow Result',
                    type: 'long_text',
                    availability: WorkflowSchemaActionStateItemAvailability.AfterExecution,
                } as WorkflowSchemaActionStateItem
            } else {
                flow_result = {
                    id: 'result',
                    name: 'Flow Result',
                    type: 'group',
                    availability: WorkflowSchemaActionStateItemAvailability.AfterExecution,
                    items: convertOutputSchemaToOutputs(returnSchema),
                } as WorkflowSchemaStateItemGroup
            }

            return {
                ...type,
                inputs: type.inputs.map((input) => {
                    if (input.id === 'flow_inputs') {
                        const group = input as WorkflowSchemaNodeInputGroup
                        return {
                            ...group,
                            items: group.items.concat(dynamicInputs),
                        }
                    }
                    return input
                }),
                state: [...type.state.filter((s) => s.id !== 'result'), flow_result],
            }
        },
        [workflows, schema?.nodes]
    )
}

// Helper function to convert output schema to inputs
function convertOutputSchemaToInputs(
    schema: WorkflowOutputSchema
): (WorkflowSchemaNodeInput | WorkflowSchemaNodeInputGroup)[] {
    return schema.schema.map((node) => {
        if (node.type === 'object') {
            return {
                id: node.id,
                name: node.id,
                type: 'group',
                items: convertOutputSchemaToInputs({ schema: node.children }),
            } as WorkflowSchemaNodeInputGroup
        } else if (node.type === 'array') {
            // For arrays, we'll treat them as long_text for simplicity
            return {
                id: node.id,
                name: node.id,
                type: 'long_text',
            } as WorkflowSchemaNodeInput
        } else {
            // This is a literal node, so it has extra_options
            const literalNode = node as WorkflowOutputLiteralNode
            return {
                id: literalNode.id,
                name: literalNode.id,
                type: literalNode.type as WorkflowSchemaStateValueType,
                extra_options: literalNode.extra_options,
            } as WorkflowSchemaNodeInput
        }
    })
}
